import type { FailReasonValues, VerificationResultValues } from '@repo/common';

import { Heading, PrimaryButton, SecondaryButton } from '@dot/design-system';

import * as SC from './CaptureServerErrorOverlayStyles';

import { useResultText } from '@/components/verification-result/hooks/use-result-text';
import { useResolveVerification } from '@/hooks/use-resolve-verification';
import * as GSC from '@/styles/GlobalStyles';

export type CaptureServerErrorOverlayProps = {
  failReason: FailReasonValues;
  message?: string | null;
  onRetry: () => void;
  retryable?: boolean;
  unverifiedUrl: string;
};

export const CaptureServerErrorOverlay = ({
  failReason,
  message,
  onRetry,
  retryable = false,
  unverifiedUrl,
}: CaptureServerErrorOverlayProps) => {
  const { redirectButton, retryButton, subtitle, title } = useResultText(
    failReason as unknown as VerificationResultValues,
  );

  const showRetryButton = retryable && !!onRetry;
  const retryLabel = retryButton ?? 'Try again';
  const displaySubtitle = message?.trim() ? message : (subtitle ?? '');

  const resolveFailedVerification = useResolveVerification({
    payload: { result: 'error' },
    url: unverifiedUrl,
  });

  return (
    <GSC.InfoContainer style={{ pointerEvents: 'auto' }}>
      <GSC.DarkOverlay />
      <SC.Instruction>
        <Heading as="h3">{title}</Heading>
        <SC.Subtitle>{displaySubtitle}</SC.Subtitle>
        <SC.ButtonRow>
          {redirectButton ? (
            <SecondaryButton colorVariant="aqua" onClick={resolveFailedVerification} size="sm" type="button">
              {redirectButton}
            </SecondaryButton>
          ) : null}
          {showRetryButton ? (
            <PrimaryButton colorVariant="aqua" onClick={onRetry} size="sm" type="button">
              {retryLabel}
            </PrimaryButton>
          ) : null}
        </SC.ButtonRow>
      </SC.Instruction>
    </GSC.InfoContainer>
  );
};
